import React from 'react';
import { Column } from 'new/elements/Table/types';
import { ChipBadge } from 'new/elements/ChipBadge';
import { AdminOrg, OrgTier } from 'new/stores/adminOrgs/adminOrgs.types';
import { ColumnFilterHeader } from './ColumnFilterHeader';
import {
  buildPlanFilterOptions,
  buildStatusFilterOptions,
  daysUntil,
  formatLimit,
  ORG_STATUS_CHIP_COLOR,
  ORG_STATUS_LABEL,
  orgInitial,
  StatusFilterValue,
} from './utils';
import styles from './CustomerOrgs.module.scss';

function OrgCell({ org }: { org: AdminOrg }) {
  return (
    <div className={styles.orgCell}>
      <div className={styles.orgAvatar}>{orgInitial(org.name)}</div>
      <div>
        <div className={styles.orgName}>{org.name}</div>
        <div className={styles.orgSubline}>
          {org.email_domain} · org_{org.id}
        </div>
      </div>
    </div>
  );
}

function StatusCell({ org }: { org: AdminOrg }) {
  const trialDays = org.status === 'TRIAL_EXPIRED' ? null : daysUntil(org.trial_expires_at);
  // Not gated on self_signup — an admin-created org with a trial-tier assigned still gets
  // a real trial_expires_at (backend auto-computes it from the tier's trial_period_days at
  // creation), even though self_signup itself can never be set by this tool (it's backend
  // read-only). The date is real and worth showing regardless of how the org originated.
  const showTrialDays = trialDays !== null;

  return (
    <div>
      <ChipBadge
        dot
        color={ORG_STATUS_CHIP_COLOR[org.status]}
        label={ORG_STATUS_LABEL[org.status]}
      />
      {showTrialDays && (
        <div className={trialDays <= 7 ? styles.trialDaysLeftWarning : styles.trialDaysLeftMuted}>
          {trialDays >= 0 ? `${trialDays}d left` : `expired ${Math.abs(trialDays)}d ago`}
        </div>
      )}
    </div>
  );
}

interface BuildColumnsParams {
  tiers: OrgTier[];
  planFilter: string;
  onPlanFilterChange: (value: string) => void;
  statusFilter: StatusFilterValue;
  onStatusFilterChange: (value: StatusFilterValue) => void;
}

export function buildColumns({
  tiers,
  planFilter,
  onPlanFilterChange,
  statusFilter,
  onStatusFilterChange,
}: BuildColumnsParams): Column<AdminOrg>[] {
  return [
    {
      name: 'Organization',
      field: 'name',
      sortable: true,
      width: 28,
      renderRow: (org) => <OrgCell org={org} />,
    },
    {
      name: 'Org ID',
      field: 'id',
      numeric: true,
      sortable: true,
      width: 6,
      renderRow: (org) => <span className={styles.orgIdCell}>{org.id}</span>,
    },
    {
      name: (
        <ColumnFilterHeader
          label="Plan"
          options={buildPlanFilterOptions(tiers).map((option) => ({
            value: option.value,
            label: option.title,
            subtitle: option.subtitle,
          }))}
          value={planFilter}
          defaultValue="PAID"
          onChange={onPlanFilterChange}
        />
      ),
      field: ['org_tier', 'display_name'] as any,
      width: 15,
      renderRow: (org) =>
        org.org_tier ? (
          <ChipBadge color="primary" label={org.org_tier.display_name} />
        ) : (
          <ChipBadge color="disabled" label="No plan" />
        ),
    },
    {
      name: (
        <ColumnFilterHeader
          label="Status"
          options={buildStatusFilterOptions()}
          value={statusFilter}
          defaultValue="ACTIVE"
          onChange={onStatusFilterChange}
        />
      ),
      field: 'status',
      width: 15,
      renderRow: (org) => <StatusCell org={org} />,
    },
    {
      name: 'Source limit',
      field: ['org_tier', 'data_source_count_limit'] as any,
      numeric: true,
      width: 13,
      renderRow: (org) => formatLimit(org.org_tier?.data_source_count_limit),
    },
    {
      name: 'Records/day',
      field: ['org_tier', 'record_count_limit'] as any,
      numeric: true,
      width: 13,
      renderRow: (org) => formatLimit(org.org_tier?.record_count_limit),
    },
    {
      name: 'SSO',
      field: 'id',
      width: 10,
      renderRow: () => <span className={styles.ssoPlaceholder}>—</span>,
    },
  ];
}
